import type { Readable } from 'svelte/store'
import { get } from 'svelte/store'
import { t } from 'svelte-i18n'
import { convertSnakeCase } from './yaml.ts'

export function injectTranslation(
	translation_string: string,
	injections: Record<string, string>,
	regex: RegExp = /{{([^}]+)}}/g
): string {
	return get(t)(translation_string).replace(regex, (match, p1) => injections[p1.trim()] ?? match)
}

type TranslatableFields<T> = {
	[K in keyof T]: string
}

export function getTranslatedStrings<T extends TranslatableFields<T>>(
	t: Readable<(key: string) => string>,
	keys: Array<keyof T>
): T {
	const translate = get(t)
	const translatedStrings = {} as T

	keys.forEach((key) => {
		translatedStrings[key] = translate(key as string)
	})

	return translatedStrings
}

export function safeDisplay(
	t: Readable<(key: string) => string>,
	s: string | null | undefined
): string {
	return s === undefined || s === null || s === '' || s === 'null' || s === 'undefined'
		? get(t)('safeDisplay')
		: s
}

/**
 * Converts an enum value to its translated label string.
 *
 * Example:
 * enumToTranslatedString('Enum.TextModeInputMode', 'single_input')
 * => $t('Enum.TextModeInputMode.SingleInput')
 *
 * @param prefix - the i18n key prefix, e.g. "Enum.TextModeInputMode"
 * @param value - the enum value, e.g. "single_input"
 * @returns translated string
 */
export function enumToTranslatedString(prefix: string, value: string): string {
	return get(t)(`${prefix}.${convertSnakeCase(value, true)}`)
}
